'use client';

import { useFunnel } from '@/lib/stores/funnel.store';
import { useCallback, useEffect, useRef, useState } from 'react';
import { tw } from '@tw';
import { sendLoginCode, verifyOTP, setSessionCookie, signInWithGoogle } from '@/app/actions/auth';
import { captureAuthCompleted, captureAuthStep, type AuthSource } from '@/lib/analytics/auth';
import { useActiveAuth } from '@/lib/stores/auth-lock';
import { useHeldWork } from '@/components/system/reload-safety';
import { GoogleSignInButton } from './GoogleSignInButton';
import { isInAppBrowser } from '@/lib/utils/inAppBrowser';
import { readLastAuthMethod, writeLastAuthMethod } from '@/lib/utils/lastAuthMethod';
import { callAction, ACTION_FAILED } from '@/lib/call-action';

interface InlineAuthProps {
  /** Identifies this surface so only one code step is live at a time. */
  id: string;
  /** Which surface asked for the sign-in — the attribution on `auth_completed`,
      and the only thing that tells a quiz lead from a product-card lead. */
  source: AuthSource;
  /** Present on a storefront, absent everywhere else — it decides signup
      attribution (an off-storefront signup is simply unattributed). */
  tutorUsername?: string;
  /** The referral edge a sign-in on the mate page anchors the pair to. */
  sourceSquad?: string | null;
  prompt?: string;
  cta: string;
  tone: 'accent' | 'card';
  /** Open on the email form: the connector's reviewers sign in with an email and a fixed code. */
  emailFirst?: boolean;
  /**
   * Continues the interrupted intent in place after a verified sign-in.
   * Optional: the cookie write already re-renders the whole tree, so a
   * surface whose signed-in state comes from the server needs neither this
   * nor a `router.refresh()`.
   */
  onSuccess?: () => void | Promise<void>;
}

export function InlineAuth({
  id,
  source,
  tutorUsername,
  sourceSquad,
  prompt,
  cta,
  tone,
  emailFirst,
  onSuccess,
}: InlineAuthProps) {
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [recheck, setRecheck] = useState(false);
  const activeAuthId = useActiveAuth((state) => state.activeAuthId);
  const claimAuth = useActiveAuth((state) => state.claimAuth);
  const onCodeStep = step === 'code' && (activeAuthId === null || activeAuthId === id);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // On a funnel page the sign-in names its quiz and its DM reply (see `useFunnel`).
  const funnel = useFunnel((s) =>
    s.funnel && s.funnel.tutorUsername === tutorUsername ? s.funnel : null
  );
  const sourceQuiz = funnel?.quizId ?? null;
  const sourceReply = funnel?.replyId ?? null;

  // One door at a time: either the Google button or the email form is on
  // screen, with a text link to the other — never both, never an "or" divider.
  // The entry default is Google; a browser whose last verified sign-in was
  // email opens on email instead (`readLastAuthMethod`). Inside Instagram/
  // TikTok-style webviews Google refuses to run (`disallowed_useragent`), so
  // there the email door is the only one and the link is dropped too. Both
  // facts are browser-only, so they're read after mount — reading them during
  // render would mismatch the server HTML.
  const [door, setDoor] = useState<'google' | 'email'>(emailFirst ? 'email' : 'google');
  const [googleAllowed, setGoogleAllowed] = useState(true);
  // Did they flip away from the entry default? Decides the link's voice:
  // "Use email instead" offers the other door; "Never mind — use Google"
  // walks back a choice just made.
  const [doorFlipped, setDoorFlipped] = useState(false);
  useEffect(() => {
    if (isInAppBrowser()) {
      setGoogleAllowed(false);
      setDoor('email');
      return;
    }
    if (readLastAuthMethod() === 'email') setDoor('email');
  }, []);

  const onGoogleDoor = door === 'google' && googleAllowed;

  const onAccent = tone === 'accent';
  const inputCls = tw(
    'rounded-[10px] px-3.5 py-2.5 text-[16px] outline-none sm:text-p-3',
    onAccent
      ? 'border border-white/40 bg-white/95 text-[#16110f] placeholder:text-[#16110f]/40'
      : 'border-[1.5px] border-[var(--ia-line)] bg-[var(--ia-field)] text-[var(--ia-ink)] placeholder:text-[var(--ia-faint)]'
  );
  const bodyCls = onAccent ? 'text-white/85' : 'text-[var(--ia-mut)]';
  const fineCls = onAccent ? 'text-white/70' : 'text-[var(--ia-faint)]';
  const errorSlot = error ? (
    <span
      role="alert"
      className={tw('text-p-3 font-semibold', onAccent ? 'text-[#ffd2d2]' : 'text-error')}
    >
      {error}
    </span>
  ) : null;
  const errorFieldCls = onAccent ? 'border-[#ffb4b4] bg-white' : 'border-error';

  // A sign-in mid-code is work the URL can't rebuild: the deploy reload
  // (VersionWatcher) would wipe the typed email and orphan the code that was
  // just sent. Held for exactly the code step.
  const hold = useHeldWork((state) => state.hold);
  useEffect(() => {
    if (step !== 'code') return;
    return hold();
  }, [step, hold]);

  // Mounting the form is the start of the sign-in: on the cards that keep it
  // behind a button, this component only exists once that button is tapped.
  useEffect(() => {
    captureAuthStep('auth_form_shown', {
      source,
      sourceTutor: tutorUsername ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emailRef = useRef<HTMLInputElement>(null);
  const keyboardHold = useRef<HTMLInputElement | null>(null);
  const releaseKeyboard = useCallback(() => {
    keyboardHold.current?.remove();
    keyboardHold.current = null;
  }, []);
  useEffect(() => {
    if (onCodeStep) releaseKeyboard();
  }, [onCodeStep, releaseKeyboard]);
  useEffect(() => releaseKeyboard, [releaseKeyboard]);

  const submitEmail = useCallback(async () => {
    if (busy) return;
    const trimmed = email.trim();
    if (!/^\S+@\S+\.\S+$/.test(trimmed)) {
      setError('Enter a valid email');
      return;
    }
    // iOS opens the keyboard only for a focus made inside the tap, and the code
    // field mounts after an await. Park focus on a numeric proxy now so the
    // code field's autoFocus inherits an open keyboard.
    const proxy = document.createElement('input');
    proxy.inputMode = 'numeric';
    proxy.setAttribute('aria-hidden', 'true');
    proxy.style.cssText = 'position:fixed;top:0;left:0;opacity:0;font-size:16px;';
    document.body.append(proxy);
    proxy.focus();
    keyboardHold.current = proxy;
    setBusy(true);
    setError(null);
    const sent = await callAction(() => sendLoginCode(trimmed));
    setBusy(false);
    if (!sent.success) {
      emailRef.current?.focus();
      releaseKeyboard();
      setError(sent.error || 'Could not send the code — try again');
      return;
    }
    captureAuthStep('auth_email_submitted', {
      source,
      sourceTutor: tutorUsername ?? null,
    });
    claimAuth(id);
    setRecheck(false);
    setStep('code');
  }, [busy, email, claimAuth, id, source, tutorUsername, releaseKeyboard]);

  const completeSignIn = useCallback(
    async (accessToken: string) => {
      const set = await callAction(() => setSessionCookie(accessToken));
      if (!set.success) {
        setBusy(false);
        setError(ACTION_FAILED);
        return;
      }
      await onSuccess?.();
      setBusy(false);
    },
    [onSuccess]
  );

  const submitCode = useCallback(
    async (codeToVerify: string) => {
      if (!/^\d{6}$/.test(codeToVerify)) return;
      setBusy(true);
      setError(null);
      const sourceTutor = tutorUsername ?? null;
      const verified = await callAction(() =>
        verifyOTP(
          email.trim(),
          codeToVerify,
          sourceSquad ?? null,
          sourceTutor,
          sourceQuiz,
          sourceReply
        )
      );
      if (!verified.success) {
        setBusy(false);
        setError(verified.error || 'Invalid code — try again');
        setCode('');
        captureAuthStep('auth_code_rejected', { source, sourceTutor });
        return;
      }
      writeLastAuthMethod('email');
      captureAuthCompleted({
        sourceTutor,
        source,
        method: 'otp',
        isNewUser: verified.data.isNewUser,
      });
      await completeSignIn(verified.data.accessToken);
    },
    [email, tutorUsername, sourceSquad, sourceQuiz, sourceReply, source, completeSignIn]
  );

  const submitGoogle = useCallback(
    async (idToken: string) => {
      setBusy(true);
      setError(null);
      const sourceTutor = tutorUsername ?? null;
      const verified = await callAction(() =>
        signInWithGoogle(idToken, sourceSquad ?? null, sourceTutor, sourceQuiz, sourceReply)
      );
      if (!verified.success) {
        setBusy(false);
        setError(verified.error || 'Google sign-in failed — try again');
        return;
      }
      writeLastAuthMethod('google');
      captureAuthCompleted({
        sourceTutor,
        source,
        method: 'google',
        isNewUser: verified.data.isNewUser,
      });
      await completeSignIn(verified.data.accessToken);
    },
    [tutorUsername, sourceSquad, sourceQuiz, sourceReply, source, completeSignIn]
  );

  return (
    <div className="flex flex-col gap-2">
      {!onCodeStep ? (
        <>
          {(recheck || prompt) && (
            <span className={tw('text-p-3', bodyCls)}>{recheck ? 'Wrong email?' : prompt}</span>
          )}
          {onGoogleDoor ? (
            <>
              {/* The GIS button rendered visibly, themed into the surface's
                  palette. It cannot wear our chrome: GIS occlusion protection
                  ignores clicks on a hidden/covered button, so an invisible
                  overlay under a styled face silently dead-ends. */}
              <div className={tw(busy && 'pointer-events-none opacity-60')}>
                <GoogleSignInButton
                  theme={onAccent ? 'outline' : 'filled_black'}
                  onSuccess={(idToken) => void submitGoogle(idToken)}
                  onError={setError}
                />
              </div>
              {busy && <span className={tw('text-center text-p-3', fineCls)}>Signing in…</span>}
              {errorSlot}
            </>
          ) : (
            <>
              <input
                ref={emailRef}
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                }}
                onKeyDown={(e) => e.key === 'Enter' && void submitEmail()}
                aria-invalid={!!error}
                className={tw(inputCls, error && errorFieldCls)}
              />
              {errorSlot}
              <button
                type="button"
                disabled={busy}
                onClick={() => void submitEmail()}
                className={tw(
                  'mm-btn rounded-xl py-3.5 text-head-3 font-semibold disabled:opacity-60',
                  onAccent
                    ? 'bg-white/95 text-[#16110f]'
                    : 'bg-[var(--ia-ink)] text-[var(--ia-field)]'
                )}
              >
                {busy ? 'Sending…' : cta}
              </button>
            </>
          )}
          {googleAllowed && (
            <button
              type="button"
              onClick={() => {
                setDoor(onGoogleDoor ? 'email' : 'google');
                setDoorFlipped((flipped) => !flipped);
                setError(null);
              }}
              className={tw('self-start text-p-3 font-semibold underline', fineCls)}
            >
              {onGoogleDoor
                ? doorFlipped
                  ? 'Never mind — use email'
                  : 'Use email instead'
                : doorFlipped
                  ? 'Never mind — use Google'
                  : 'Use Google instead'}
            </button>
          )}
        </>
      ) : (
        <>
          <span className={tw('text-p-3', bodyCls)}>
            Code sent to <span className="break-all font-semibold">{email.trim()}</span>
          </span>
          <label className="relative block">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
              value={code}
              onChange={(e) => {
                const next = e.target.value.replace(/\D/g, '').slice(0, 6);
                setCode(next);
                setError(null);
                if (next.length === 6) void submitCode(next);
              }}
              aria-invalid={!!error}
              aria-label="Verification code"
              className="absolute inset-0 h-full w-full cursor-pointer text-[16px] opacity-0"
            />
            <span aria-hidden className="flex gap-1.5">
              {Array.from({ length: 6 }, (_, i) => {
                const char = code[i];
                const isNext = i === code.length;
                return (
                  <span
                    key={i}
                    className={tw(
                      'sf-num flex flex-1 items-center justify-center border-[1.5px] font-extrabold transition-all duration-150',
                      'h-12 rounded-xl text-head-2',
                      char && 'scale-[1.04]',
                      onAccent
                        ? 'border-white/40 bg-white/95 text-[#16110f]'
                        : 'border-[var(--ia-line)] bg-[var(--ia-field)] text-[var(--ia-ink)]',
                      isNext && !error && (onAccent ? 'border-white' : 'border-[var(--sf-ink)]'),
                      error && (onAccent ? 'border-[#ffb4b4]' : 'border-error')
                    )}
                  >
                    {char ?? (isNext ? <span className="sf-caret" /> : '')}
                  </span>
                );
              })}
            </span>
          </label>
          {busy ? (
            <span className={tw('text-center text-p-3', fineCls)}>Checking…</span>
          ) : (
            errorSlot
          )}
          <button
            type="button"
            onClick={() => {
              setStep('email');
              setRecheck(true);
              setCode('');
              setError(null);
            }}
            className={tw('self-start text-p-3 font-semibold underline', fineCls)}
          >
            Didn&apos;t get the code?
          </button>
        </>
      )}
    </div>
  );
}
